import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { GlowingCTA } from '../ui/GlowingCTA';

export function FinalCTA() {
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-white dark:bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-full bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-purple-600/20 blur-[100px]" />
        
        {/* Animated stars/particles */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute bg-white rounded-full blur-[1px]"
            style={{
              width: Math.random() * 4 + 2 + 'px',
              height: Math.random() * 4 + 2 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[2.5rem] overflow-hidden"
        >
          {/* Glass background */}
          <div className="absolute inset-0 bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none backdrop-blur-3xl border border-gray-200 dark:border-[#1E2A3A]" />
          
          {/* Inner gradient glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10" />

          <div className="relative px-6 py-16 sm:py-20 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none text-slate-900 dark:text-slate-200 text-sm font-bold mb-8 shadow-sm backdrop-blur-md"
            >
              <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Start in 60 seconds
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-[1.1] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Ready to Accelerate Your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Business Growth?
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-slate-900 dark:text-slate-300 mb-10 max-w-2xl mx-auto"
            >
              Join 10,000+ MSMEs using our AI-powered platform for compliance, finance, and marketing.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <GlowingCTA onClick={() => navigate('/packages')} className="px-12 py-5 text-lg w-full sm:w-auto">
                Explore Packages <ArrowRight className="w-5 h-5 ml-2 inline" />
              </GlowingCTA>
              
              <button
                onClick={() => navigate('/tools')}
                className="px-10 py-5 rounded-full bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none text-slate-900 dark:text-white font-bold text-lg hover:bg-gray-200 dark:bg-[#111C2F] transition-colors w-full sm:w-auto"
              >
                Try Free Tools
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
